import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { useSearchParams } from "react-router-dom";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import ToolShell from "./ToolShell";
import { Panel, NumField, Stat } from "./ToolUI";
import { num, pct, saveResult, tierFor, loadResults } from "./toolkit";

const DEFAULTS = { shift: "480", breaks: "60", downtime: "47", ideal: "1.0", total: "19271", rejects: "423" };
const WC = { a: 0.9, p: 0.95, q: 0.999, oee: 0.85 };

const compute = (f) => {
  const ppt = Math.max(0, num(f.shift) - num(f.breaks));
  const run = Math.max(0, ppt - num(f.downtime));
  const total = num(f.total);
  const good = Math.max(0, total - num(f.rejects));
  const idealMin = (num(f.ideal) * total) / 60;
  const a = ppt ? run / ppt : 0;
  const p = run ? idealMin / run : 0;
  const q = total ? good / total : 0;
  const losses = [
    { name: "Availability (downtime)", min: num(f.downtime) },
    { name: "Performance (slow cycles, small stops)", min: Math.max(0, run - idealMin) },
    { name: "Quality (scrap & rework)", min: (num(f.ideal) * num(f.rejects)) / 60 },
  ].sort((x, y) => y.min - x.min);
  return { ppt, run, good, a, p, q, oee: a * p * q, losses };
};

const ImportBanner = ({ lossPct, minutes, ppt, highlight, onApply, onDismiss }) => (
  <div className={`border rounded-sm p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 no-print animate-in fade-in slide-in-from-top-2 duration-500 ${highlight ? "border-primary bg-primary/10" : "border-border bg-card"}`} data-testid="oee-import-banner">
    <Download className="w-5 h-5 text-primary shrink-0" />
    <div className="flex-1 text-sm">
      <div className="font-semibold">Import from Downtime Cost</div>
      <div className="text-muted-foreground" data-testid="oee-import-detail">
        Your downtime result: <b className="text-foreground">{lossPct.toFixed(1)}%</b> of planned time lost → <b className="text-foreground">{minutes} min</b> of a {ppt}-min shift.
      </div>
    </div>
    <div className="flex gap-2">
      <Button onClick={onApply} className="rounded-sm h-10 bg-primary hover:bg-primary/90" data-testid="oee-import-apply">Apply</Button>
      <Button onClick={onDismiss} variant="ghost" className="rounded-sm h-10" data-testid="oee-import-dismiss"><X className="w-4 h-4 mr-1" />Dismiss</Button>
    </div>
  </div>
);

export default function OEECalculator() {
  const [f, setF] = useState(DEFAULTS);
  const [touched, setTouched] = useState(false);
  const set = (k) => (v) => { setTouched(true); setF((s) => ({ ...s, [k]: v })); };
  const r = useMemo(() => compute(f), [f]);
  const [params] = useSearchParams();
  const [dt] = useState(() => loadResults().downtime);
  const [bannerOpen, setBannerOpen] = useState(!!dt);
  const [applied, setApplied] = useState(false);
  const importMin = dt ? Math.round((dt.lossPct / 100) * r.ppt) : 0;
  const applyImport = () => { set("downtime")(String(importMin)); setBannerOpen(false); setApplied(true); };
  const score = r.oee * 100;
  const tier = tierFor(score);
  const gapParts = Math.max(0, Math.round(((WC.oee - r.oee) * r.ppt * 60) / Math.max(num(f.ideal), 0.0001)));

  useEffect(() => {
    if (touched) saveResult("oee", { score, a: r.a, p: r.p, q: r.q });
  }, [touched, score, r.a, r.p, r.q]);

  const chart = [
    { name: "Availability", You: +(r.a * 100).toFixed(1), "World-class": 90 },
    { name: "Performance", You: +(r.p * 100).toFixed(1), "World-class": 95 },
    { name: "Quality", You: +(r.q * 100).toFixed(1), "World-class": 99.9 },
    { name: "OEE", You: +score.toFixed(1), "World-class": 85 },
  ];

  const share = {
    slug: "oee-calculator", tool: "OEE Score", scoreText: `${score.toFixed(1)}%`, buttonLabel: "Share my OEE score",
    label: `A ${pct(r.a)} · P ${pct(r.p)} · Q ${pct(r.q)} — ${tier.label}`,
    bar: { value: score, target: 85, targetLabel: "World-class 85%" },
    tagline: score >= 85 ? "World-class — and staying there with RuleAI" : "Working to hit 85% with RuleAI",
    caption: `Our line is running at ${score.toFixed(1)}% OEE. World-class is 85% — working to close the gap with RuleAI.`,
  };

  return (
    <ToolShell toolKey="oee" overline="Free tool · OEE" title="OEE Calculator"
      intro="Overall Equipment Effectiveness = Availability × Performance × Quality. Enter one shift of data — the example below is pre-filled so you can see how it works."
      share={share} results={{ oee: +score.toFixed(2), availability: r.a, performance: r.p, quality: r.q, inputs: f }}>
      {dt && bannerOpen && (
        <ImportBanner lossPct={dt.lossPct} minutes={importMin} ppt={r.ppt} highlight={params.get("from") === "downtime"}
          onApply={applyImport} onDismiss={() => setBannerOpen(false)} />
      )}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6">
        <Panel overline="01 / Inputs" title="One shift of data" testid="oee-inputs">
          <div className="grid sm:grid-cols-2 gap-4">
            <NumField label="Shift length" suffix="min" value={f.shift} onChange={set("shift")} testid="oee-shift" />
            <NumField label="Planned breaks" suffix="min" value={f.breaks} onChange={set("breaks")} testid="oee-breaks" />
            <NumField label="Unplanned downtime" suffix="min" value={f.downtime} onChange={set("downtime")} testid="oee-downtime"
              hint={applied ? <span className="text-primary" data-testid="oee-import-applied">Imported from Downtime Cost</span> : null} />
            <NumField label="Ideal cycle time" suffix="sec / part" value={f.ideal} onChange={set("ideal")} testid="oee-ideal" />
            <NumField label="Total parts made" suffix="pcs" value={f.total} onChange={set("total")} testid="oee-total" />
            <NumField label="Rejected parts" suffix="pcs" value={f.rejects} onChange={set("rejects")} testid="oee-rejects" />
          </div>
          <div className="mt-5 text-xs text-muted-foreground font-mono-plex space-y-1">
            <div>Planned production time: {r.ppt} min · Run time: {r.run} min · Good parts: {r.good.toLocaleString()}</div>
            {r.p > 1 && <div className="text-amber-400">Performance &gt; 100% means more parts than the run time allows at your ideal cycle time — check downtime, parts count or ideal cycle time. (OEE = good parts × ideal cycle ÷ planned time.)</div>}
          </div>
        </Panel>
        <Panel overline="02 / Results" title="Your OEE vs. world-class" testid="oee-results">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2 sm:col-span-4"><Stat big label="OEE" value={`${score.toFixed(1)}%`} sub={tier.label} tone={tier.tone} testid="oee-score" /></div>
            <Stat label="Availability" value={pct(r.a)} sub="WC 90%" testid="oee-availability" />
            <Stat label="Performance" value={pct(r.p)} sub="WC 95%" testid="oee-performance" />
            <Stat label="Quality" value={pct(r.q)} sub="WC 99.9%" testid="oee-quality" />
            <Stat label="Gap to 85%" value={gapParts.toLocaleString()} sub="parts / shift" testid="oee-gap" />
          </div>
          <div className="h-60 mt-5">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid stroke="#27272a" vertical={false} />
                <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="You" fill="#3b82f6" />
                <Bar dataKey="World-class" fill="#ea580c" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
      <Panel overline="03 / Where the time goes" title="Your biggest losses (minutes per shift)" testid="oee-losses">
        <div className="space-y-3">
          {r.losses.map((l, i) => (
            <div key={l.name} data-testid={`oee-loss-${i}`}>
              <div className="flex justify-between text-sm"><span>{i === 0 && <span className="text-accent font-semibold mr-2">Biggest</span>}{l.name}</span><span className="font-mono-plex">{l.min.toFixed(0)} min</span></div>
              <div className="h-2 bg-muted rounded-sm mt-1 overflow-hidden"><div className={`h-full ${i === 0 ? "bg-accent" : "bg-primary/60"}`} style={{ width: `${r.ppt ? Math.min(100, (l.min / r.ppt) * 100 * 3) : 0}%` }} /></div>
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-5">Start with the biggest bucket. Availability losses respond to SMED and preventive maintenance; performance losses to small-stop tracking; quality losses to root-cause CAPA.</p>
      </Panel>
    </ToolShell>
  );
}
